import React, { useRef, useEffect } from "react";

/* 9:16 aspect ratio sizes — width is the controlling dimension */
const SIZES = {
  sm:  { w: 28, h: 50 },
  md:  { w: 40, h: 71 },
  lg:  { w: 54, h: 96 },
  xl:  { w: 80, h: 142 },
  fab: { w: 56, h: 100 },
};

/**
 * AethelAura animated mascot — 9:16 portrait video, no circular crop.
 * Respects prefers-reduced-motion by showing static poster.
 */
export default function AethelAuraIcon({ size = "md", animate = true, expression = "wave" }) {
  const dim = SIZES[size] || SIZES.md;
  const videoRef = useRef(null);
  const isSmall = size === "sm" || size === "md";
  const videoSrc = isSmall ? "/aethel-aura-wave-sm.mp4" : "/aethel-aura-wave.mp4";
  const posterSrc = isSmall ? "/aethel-aura-poster-sm.jpg" : "/aethel-aura-poster.jpg";

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !animate) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (mq.matches) { video.pause(); return; }
    video.play().catch(() => {});
  }, [animate]);

  const borderRadius = size === "sm" ? 6 : size === "fab" ? 16 : 12;

  if (!animate) {
    return (
      <div style={{ width: dim.w, height: dim.h, flexShrink: 0, lineHeight: 0 }} data-testid={`aura-icon-${expression}`}>
        <img
          src={posterSrc}
          alt="AethelAura"
          width={dim.w}
          height={dim.h}
          className="select-none pointer-events-none"
          style={{ width: dim.w, height: dim.h, objectFit: "cover", borderRadius }}
          draggable={false}
        />
      </div>
    );
  }

  return (
    <div
      className={`aethel-aura-icon ${expression === "think" ? "aethel-aura-think" : ""} ${expression === "celebrate" ? "aethel-aura-celebrate" : ""}`}
      style={{ width: dim.w, height: dim.h, flexShrink: 0, lineHeight: 0, position: "relative" }}
      data-testid={`aura-icon-${expression}`}
    >
      <video
        ref={videoRef}
        src={videoSrc}
        poster={posterSrc}
        width={dim.w}
        height={dim.h}
        autoPlay
        loop
        muted
        playsInline
        preload={size === "fab" ? "auto" : "metadata"}
        className="select-none pointer-events-none"
        style={{
          width: dim.w,
          height: dim.h,
          objectFit: "cover",
          borderRadius,
        }}
        aria-label="AethelAura animated mascot"
      />

      {expression === "think" && (
        <div className="aura-think-dots" style={{ position: "absolute", top: "6%", right: "2%" }}>
          <span /><span /><span />
        </div>
      )}

      {expression === "celebrate" && (
        <>
          <span className="aura-sparkle aura-sparkle-1" />
          <span className="aura-sparkle aura-sparkle-2" />
          <span className="aura-sparkle aura-sparkle-3" />
        </>
      )}
    </div>
  );
}
